import { uniq } from 'lodash'
import { groupBy, intersection, uniqBy } from '@ota-frontend/core/utils'

export default class Facets {
  constructor(
    facets = {
      air: [],
      train: [],
      hotel: [],  // { code: String, name: String, rank: Number, city: String }
      region: [],
      country: [],
      city: [],
      district: [],
      intentions: {
        accommodation_area: [],
        hotel_type: [],
        room_facility: [],
        meal: [],
        theme: [],
        other: [],
      },
      popular_area: {
        region: [],
        country: [],
        city: [],
        district: [],
      },
    },
    isDomestic = false,  // 国内国外で項目名が微妙に変わる
    options = {},
  ) {
    const defaultOptions = {
      originalPopularIntentions: undefined,
    }
    this.originalOptions = options
    options = {
      ...defaultOptions,
      ...options,
    }

    this.facets = Object.freeze(facets)
    this.isDomestic = isDomestic
    // LP からの流入時に設定されることのある項目
    // 暫定的に直に記述しているが、継続的に運用するなら BFF から返した方が良いかもしれない
    this.originalPopularIntentions = Object.freeze(options.originalPopularIntentions || (
      this.isDomestic
        ? [
          { codes: ['1339'], name: '2都市周遊'},
          { codes: ['542'], name: '3都市以上周遊'},
          { codes: ['1598'], name: 'レンタカー付き'},
          { codes: ['1600'], name: '温泉宿（旅館）'},
          { codes: ['912'], name: 'オーシャンビュールーム'},
        ]
        : [
          {
            codes: ['108', '1761', '1762', '1763', '1764', '1765', '1766', '1767', '1768', '1769', '1770', '1771'],
            name: 'ファミリーにおすすめ',
          },
          {
            codes: ['840', '1772', '1773', '1774', '1775', '1776', '1777', '1778', '1779', '1780', '1781', '1782'],
            name: 'ハネムーンにおすすめ',
          },
          {
            codes: ['748', '1783', '1784', '1785', '1786', '1787', '1788', '1789', '1790', '1791', '1792', '1793'],
            name: '女子旅',
          },
          {
            codes: ['797', '1728', '1729', '1730', '1731', '1732', '1733', '1734', '1735', '1736', '1737', '1738'],
            name: 'ホテル-空港間の送迎付き',
          },
          { codes: ['912'], name: 'オーシャンビュールーム'},
        ]
    ))
  }

  get raw() {
    return this.facets
  }

  get asDomestic() {
    return new Facets(this.facets, true, this.originalOptions)
  }

  get asAbroad() {
    return new Facets(this.facets, false, this.originalOptions)
  }

  get airs() {
    return this.facets.air
  }

  get trains() {
    return this.isDomestic
      ? (this.facets.train || []).map(t => ({
        ...t,
        name: transportationLabelAlternatives[t.code] || t.name,
      }))
      : this.facets.train || []
  }

  get airlines() {
    return this.isDomestic
      ? (this.facets.air || []).map(a => ({
        ...a,
        name: transportationLabelAlternatives[a.code] || a.name,
      }))
      : this.facets.air || []
  }

  get transportations() {
    return [...this.trains, ...this.airlines]
  }

  isTransportationSelectable(code) {
    return this.transportations.some(t => t.code === code)
  }

  get hotels() {
    return this.facets.hotel
  }

  findHotelByCode(code) {
    return this.hotels.find(h => h.code === code)
  }

  filterHotelsByCodes(codes) {
    return this.hotels.filter(({ code }) => codes.includes(code))
  }

  filterHotelsByCity(cityId) {
    return this.hotels.filter(h => h.city === String(cityId))
  }

  isHotelSelectable(code) {
    // 本当は件数判定したいが、tour_api /count が返す結果がおかしく、いずれかのホテルを選んだ状態だとほとんどの別ホテルが候補に挙がらなくなる
    return true
  }

  reasonWhyNoHotelsAtDestination(index) {
    return (((((this.facets || {})
      .additional_information || {})
      .hotel || {})
      .was_unsearchable_at_destination || [])[index] || {})
      .reason
  }

  get regions() {
    return this.facets.region || []
  }

  get popularRegions() {
    return (this.facets.popular_area || {}).region || []
  }

  get countries() {
    return this.facets.country || []
  }

  get popularCountries() {
    return (this.facets.popular_area || {}).country || []
  }

  get cities() {
    return this.facets.city || []
  }

  get popularCities() {
    return (this.facets.popular_area || {}).city || []
  }

  get districts() {
    return this.facets.district || []
  }

  get popularDistricts() {
    return (this.facets.popular_area || {}).district || []
  }

  get _intentions() {
    return this.facets.intentions || {}
  }

  // 同一 name のこだわり条件もかまわず平坦化した全ての ID が返る
  get intentionIds() {
    return uniq([
      ...this.popularIntentionIds,
      ...(this._intentions.accommodation_area || []).map(i => i.code),
      ...(this._intentions.hotel_type || []).map(i => i.code),
      ...(this._intentions.room_facility || []).map(i => i.code),
      ...(this._intentions.meal || []).map(i => i.code),
      ...(this._intentions.theme || []).map(i => i.code),
      ...(this._intentions.other || []).map(i => i.code),
    ])
  }

  // 同一 name でグループ化済みのこだわり条件が返る
  // [{ name: ..., codes: [...] }, ...]
  get intentions() {
    return uniqBy([
      ...this.popularIntentions,
      ...this.accommodationAreas,
      ...this.hotelTypes,
      ...this.roomFacilities,
      ...this.meals,
      ...this.themes,
      ...this.otherIntentions,
    ], 'name')
  }

  // LP からの流入時に設定されることのある項目
  // 暫定的に直に記述しているが、継続的に運用するなら BFF から返した方が良いかもしれない
  get popularIntentions() {
    const intentions = [
      ...this.accommodationAreas,
      ...this.hotelTypes,
      ...this.roomFacilities,
      ...this.meals,
      ...this.themes,
    ]
    return this.originalPopularIntentions
      .map(item => intentions.find(i => i.name === item.name) || item)
  }

  /**
   * 人気の絞り込み条件のID
   * @member {string[]} 人気の絞り込み条件のintention_idの配列
   */
  get popularIntentionIds() {
    return this.popularIntentions.flatMap(p => p.codes)
  }

  get withoutPopularIntentions() {
    return new Facets(this.facets, this.isDomestic, { originalPopularIntentions: [] })
  }

  get accommodationAreas() {
    return this._bundleByName(this._intentions.accommodation_area)
  }

  get hotelTypes() {
    return this._bundleByName(this._intentions.hotel_type)
  }

  get roomFacilities() {
    return this._bundleByName(this._intentions.room_facility)
  }

  get meals() {
    return this._bundleByName(this._intentions.meal)
  }

  get themes() {
    return this._bundleByName(this._intentions.theme)
  }

  get otherIntentions() {
    return this._bundleByName(this._intentions.other)
  }

  isIntentionSelectable(codes) {
    return intersection(codes, this.intentionIds).length > 0
  }

  findIntentionNameById(id) {
    const intention = this.intentions.find(i => i.codes.includes(id))
    return intention ? intention.name : ''
  }

  _bundleByName(intentions) {
    return Object.values(groupBy(intentions || [], 'name'))
      .map(group => ({
        codes: group.map(item => item.code),
        name: group[0].name,
      }))
  }

  get seats() {
    return [
      { name: '指定なし', code: null },
      { name: 'エコノミー', code: '0' },
      { name: 'ビジネス', code: '1' },
      { name: 'ファースト', code: '2' },
      { name: 'プレミアムエコノミー', code: '3' },
    ]
  }

  get outwards() {
    return this.isDomestic
      ? [
        { name: '出発', code: null },
        { name: '午前発(08:00～)', code: '1' },
        { name: '午後発(12:00～)', code: '2' },
        { name: '夜発(18:00～)', code: '3' },
        { name: '早朝発(04:00～)', code: '4' },
        { name: '深夜発(23:00～)', code: '5' },
      ]
      : [
        { name: '日本出発', code: null },
        { name: '午前発(08:00～)', code: '1' },
        { name: '午後発(12:00～)', code: '2' },
        { name: '夜発(18:00～)', code: '3' },
        { name: '早朝発(04:00～)', code: '4' },
        { name: '深夜発(23:00～)', code: '5' },
      ]
  }

  get arrivals() {
    return [
      { name: '現地到着', code: null },
      { name: '午前着(08:00～)', code: '1' },
      { name: '午後着(12:00～)', code: '2' },
      { name: '夜着(18:00～)', code: '3' },
      { name: '早朝着(04:00～)', code: '4' },
      { name: '深夜着(23:00～)', code: '5' },
    ]
  }

  get homewards() {
    return [
      { name: '現地出発', code: null },
      { name: '午前発(08:00～)', code: '1' },
      { name: '午後発(12:00～)', code: '2' },
      { name: '夜発(18:00～)', code: '3' },
      { name: '早朝発(04:00～)', code: '4' },
      { name: '深夜発(23:00～)', code: '5' },
    ]
  }

  get returns() {
    return [
      { name: '日本到着', code: null },
      { name: '午前着(08:00～)', code: '1' },
      { name: '午後着(12:00～)', code: '2' },
      { name: '夜着(18:00～)', code: '3' },
      { name: '早朝着(04:00～)', code: '4' },
      { name: '深夜着(23:00～)', code: '5' },
    ]
  }
}

const transportationLabelAlternatives = {
  '1': 'JAL（飛行機）',
  '2': 'ANA（飛行機）',
  '215': 'ジェットスター（飛行機）',
  '196': 'Peach（飛行機）',
  '85': 'スカイマーク（飛行機）',
  'JR': 'JR（新幹線）',
}
